import { useGetCompanies } from "@/entities/get-companies/fetch";
import { useGetVehicle } from "@/entities/get-vehicle/fetch";
import { usePatchVehicle } from "@/entities/patch-vehicle/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { VEHICLE_EQIUIPMENTS } from "@/shared/constants/vehicle";
import { excludeNull } from "@/shared/lib/collection";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { transformLoadable } from "@/shared/lib/loadable";
import { safeParseInt } from "@/shared/lib/number";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { MasterDataVehicleDetailPageProps } from "../model";

export const useShipperContractCreatProps = (
  vehicleIdStr: string | undefined,
): MasterDataVehicleDetailPageProps => {
  // Auth
  const { session } = useAuth();

  // State
  const [previewValues, setPreviewValues] =
    useState<MasterDataVehicleDetailPageProps["previewValues"]>(undefined);

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      MasterDataVehicleDetailPageProps["defaultInputValues"]
    >("MasterDataVehicleDetailPage");

  // Backend API
  const vehicleId = safeParseInt(vehicleIdStr);
  const getVehicleQuery = useGetVehicle(
    {
      id: vehicleId ?? -1,
    },
    {
      enabled: !!vehicleId,
    },
  );
  const getCompaniesQuery = useGetCompanies(
    {
      search: {
        name: session.isAuthenticated ? session.user.companyName : "",
      },
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: session.isAuthenticated,
    },
  );

  const patchVehicleMutation = usePatchVehicle();

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Actions
  const navigate = useNavigate();
  const onInputValuesUpdate = useCallback<
    MasterDataVehicleDetailPageProps["onInputValuesUpdate"]
  >(
    (deliveryProposalOfferCargo) => {
      setInProgressValues(deliveryProposalOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    MasterDataVehicleDetailPageProps["onConfirmClick"]
  >((deliveryProposalOfferCargo) => {
    setPreviewValues(deliveryProposalOfferCargo);
  }, []);

  const onBackToInProgressClick = useCallback<
    MasterDataVehicleDetailPageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(undefined);
  }, []);

  const onSubmitClick = useCallback<
    MasterDataVehicleDetailPageProps["onSubmitClick"]
  >(async () => {
    if (!session.isAuthenticated || !previewValues) {
      return;
    }
    if (!vehicleId) {
      setGlobalNotification({
        variant: "error",
        message: "車両IDが不正な値です",
      });
      return;
    }
    const officeId = safeParseInt(previewValues.officeId);
    if (previewValues.officeId && !officeId) {
      setGlobalNotification({
        variant: "error",
        message: "事業所IDが不正な値です",
      });
      return;
    }
    const response = await patchVehicleMutation.mutateAsync({
      id: vehicleId,
      data: {
        number: previewValues.number,
        companyId: session.user.companyId,
        officeId,
        name: previewValues.name,
        shape: previewValues.shape,
        vehicleEquipments: excludeNull(
          previewValues.vehicleEquipments?.map((value) => {
            const equipmentId = VEHICLE_EQIUIPMENTS.find(
              (equipment) => equipment.value === value,
            )?.id;
            if (!equipmentId) {
              return undefined;
            }
            return { equipmentId };
          }) ?? [],
        ),
        weightClass: previewValues.weightClass,
        memo: previewValues.memo,
        lastUpdatedUser: session.user.id,
      },
    });
    if (response && isSuccessStatusCode(response.status)) {
      setGlobalNotification({
        variant: "success",
        message: "車両登録が完了しました",
        autoHide: true,
      });
      removeInProgressValues();
      void (async () => {
        await getVehicleQuery.refetch();
        navigate({
          to: "/master-data/vehicles",
        });
      })();
    } else {
      setGlobalNotification({
        variant: "error",
        message: "車両登録に失敗しました",
      });
    }
  }, [
    session,
    vehicleId,
    previewValues,
    patchVehicleMutation.mutateAsync,
    getVehicleQuery.refetch,
    removeInProgressValues,
    setGlobalNotification,
    navigate,
  ]);

  // Props
  const offices = useMemo(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getCompaniesQuery),
      (data) => {
        return data.data.companies;
      },
    );
  }, [getCompaniesQuery]);

  const vehicle = useMemo(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getVehicleQuery),
      (data) => {
        const vehicle = data.data;
        if (!vehicle) {
          throw new Error(`車両が見つかりませんでした。 ${vehicleId}`);
        }
        return vehicle;
      },
    );
  }, [vehicleId, getVehicleQuery]);
  return {
    vehicle,
    offices,
    defaultInputValues: inProgressValues,
    previewValues,
    onInputValuesUpdate,
    onConfirmClick,
    onBackToInProgressClick,
    onSubmitClick,
  };
};
